import React, { FormEvent, useCallback, useState } from 'react'
import cn, { Argument as ClassName } from 'classnames'
import styled from 'styled-components'
import { useNavigate, useSearchParams } from 'react-router-dom'

import { isPasswordLongEnough } from '../../domain/account-rules'
import useDefer, { Status } from '../../../common/hooks/useDefer'
import Text from '../../../ui/Text'
import usePatch from '../../hooks/usePatch'
import { FormHeader } from '../../../lib/form-header'
import { LandingPageLayout, SContentWrapper, SSpacer, SNotification } from '../../../lib/landing-page-layout'
import { PasswordInput } from '../../../lib/password-input'
import resetUserPassword from '../../../lib/resetUserPassword'
import { SubmitButton } from '../../../lib/submit-button'
import storage from '../../../common/utils/storage'
import useText from '../../hooks/useText'

export type PasswordResetPageProps = {
    className?: ClassName
    style?: React.CSSProperties
}

const SFormWrapper = styled(SContentWrapper)`
    width: 100%;
    max-width: 435px;
`

export function PasswordResetPage({ className, style }: PasswordResetPageProps): React.ReactElement | null {
    const text = useText()
    const { value, apply } = usePatch({ password: '', passwordCompare: '' })
    const [error, setError] = useState('')
    const [submitError, setSubmitError] = useState(false)

    const [showPassword, toggleShowPassword] = useState(false)
    const [showPasswordCompare, toggleShowPasswordCompare] = useState(false)

    const navigate = useNavigate()

    const [searchParams] = useSearchParams()

    const { execute: resetPassword, status } = useDefer((token, value) =>
        resetUserPassword(token, value)
            .then(() => {
                storage.setItem('notification', { text: 'msgPasswordReset' })
                navigate('/login', { replace: true })
            })
            .catch(() => setSubmitError(true))
    )

    const handleSubmit = useCallback(
        (event: FormEvent<HTMLFormElement>): void => {
            event.preventDefault()

            const token = searchParams?.get('token')
            if (!token) return

            if (value.password !== value.passwordCompare) {
                setError(text('msgPasswordDoNotMatch'))
                return
            }

            if (!isPasswordLongEnough(value.password)) {
                setError(text('msgPasswordToShortSignup'))
                return
            }

            resetPassword(token, value)
        },
        [value, searchParams]
    )

    const changeValue = (
        value: Partial<{
            password: string
            passwordCompare: string
        }>
    ) => {
        setError('')
        apply(value)
    }

    return (
        <LandingPageLayout className={cn(className)} style={style} background="#ffffff" hideLanguageSelect>
            <SFormWrapper>
                {submitError && (
                    <SSpacer>
                        <SNotification data-testid="signInError" error={submitError}>
                            <Text code="msgPasswordNotChanged" />
                        </SNotification>
                    </SSpacer>
                )}
                <SSpacer>
                    <FormHeader />
                </SSpacer>

                <form onSubmit={handleSubmit} data-testid="passwordForm" noValidate>
                    <SSpacer>
                        <PasswordInput
                            label={value.password.length > 0 ? text('lblPassword') : ''}
                            placeholder={text('lblPassword')}
                            onChange={(value: string) => changeValue({ password: value })}
                            showPassword={showPassword}
                            message={!value.password ? text('msgMinimumPasswordLength') : null}
                            error={error}
                            errorTestId="passwordInputError"
                            toggleShowPasswordFlag={toggleShowPassword}
                            hasText={value.password.length > 0}
                        />
                    </SSpacer>

                    <SSpacer>
                        <PasswordInput
                            label={value.passwordCompare.length > 0 ? text('lblConfirmPasswordField') : ''}
                            placeholder={text('lblConfirmPasswordField')}
                            onChange={(value: string) => changeValue({ passwordCompare: value })}
                            showPassword={showPasswordCompare}
                            toggleShowPasswordFlag={toggleShowPasswordCompare}
                            hasText={value.passwordCompare.length > 0}
                        />
                    </SSpacer>

                    <SSpacer>
                        <SubmitButton disabled={!value.password || !value.passwordCompare || status === Status.PENDING}>
                            <Text code="btnResetPassword" />
                        </SubmitButton>
                    </SSpacer>
                </form>
            </SFormWrapper>
        </LandingPageLayout>
    )
}
